import { Controller } from "@hotwired/stimulus"
import { template, debounce } from "lodash"
import { useClickOutside } from "stimulus-use"

export default class extends Controller {
  static targets = ["input", "list", "menu", "searchResultTemplate", "selectedList", "selectedTemplate", "hiddenInputs", "hiddenInputTemplate"]

  static values = {
    departmentId: String,
    invoiceBankAccountVersionIds: Array,
    newRecord: Boolean
  }

  availableAccounts = []
  selectedAccounts = []
  invoiceOriginalAccountIds = []
  isInitialLoad = true
  searchResultTemplate = null
  selectedTemplate = null
  eventCreated = false
  loadAbortController = null

  connect() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.add("hidden")

      this.searchResultTemplate = template(this.searchResultTemplateTarget.innerHTML)
      this.selectedTemplate = template(this.selectedTemplateTarget.innerHTML)

      this.triggerSearch = debounce(this.triggerSearch, 300).bind(this)

      useClickOutside(this, {
        element: this.menuTarget
      })
    }

    // Bug D1: filtrujemy puste stringi, bo nowa faktura ma [""] a nie []
    this.invoiceOriginalAccountIds = (this.invoiceBankAccountVersionIdsValue || []).filter(id => id !== '')

    this.loadBankAccounts()

    window.addEventListener("bank-account:create", this.selectCreatedAccount.bind(this))
  }

  departmentIdValueChanged() {
    if (!this.isInitialLoad) {
      this.selectedAccounts = []
    }

    this.loadBankAccounts()
  }

  async loadBankAccounts() {
    this.loadAbortController?.abort()
    this.loadAbortController = new AbortController()
    const { signal } = this.loadAbortController

    if (!this.departmentIdValue) {
      this.availableAccounts = []
      if (this.isInitialLoad) {
        await this.loadExistingInvoiceAccounts(signal)
        this.isInitialLoad = false
      }
      this.updateDisplay()
      return
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = {
        Accept: 'application/json'
      }

      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }

      const response = await fetch(`/departments/${this.departmentIdValue}/bank_accounts`, {
        method: 'GET',
        headers,
        signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.availableAccounts = data

      const hasInvoiceAccountIds = this.invoiceOriginalAccountIds.length > 0

      if (hasInvoiceAccountIds && this.isInitialLoad) {
        this.selectedAccounts = this.availableAccounts.filter(account =>
          this.invoiceOriginalAccountIds.includes(account.bank_account_version_id)
        )

        const foundAccountIds = this.selectedAccounts.map(acc => acc.bank_account_version_id)
        const missingAccountIds = this.invoiceOriginalAccountIds.filter(id =>
          !foundAccountIds.includes(id)
        )

        if (missingAccountIds.length > 0) {
          await this.loadMissingAccounts(missingAccountIds, signal)
        }
      } else {
        this.selectedAccounts = this.availableAccounts.filter(account => account.show_on_invoice)
      }

      this.isInitialLoad = false
      this.updateDisplay()
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error("Error loading bank accounts", error)
    }
  }

  async triggerSearch(event) {
    const query = event.target.value.toLowerCase().trim()
    this.listTarget.innerHTML = ""

    try {
      const searchResults = await this.fetchBankAccounts(query)
      this.showSearchResults(searchResults)
    } catch (error) {
      console.error("Error searching bank accounts:", error)
    }
  }

  async fetchBankAccounts(query = '') {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = {
      Accept: 'application/json'
    }

    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }

    const alreadyAddedIds = this.selectedAccounts.map(account => account.bank_account_version_id)
    const params = new URLSearchParams({
      rows: '10',
      name: query,
      exclude_ids: alreadyAddedIds.join(',')
    })

    let url
    if (!this.departmentIdValue || this.departmentIdValue === '' || this.departmentIdValue === 'null') {
      url = '/find_bank_accounts'
    } else {
      url = `/departments/${this.departmentIdValue}/bank_accounts`
    }

    const response = await fetch(`${url}?${params}`, {
      method: 'GET',
      headers
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return Array.isArray(data)
      ? data.map(item => ({
        id: item.bank_account_version_id || item.id,
        bank_account_version_id: item.bank_account_version_id || item.id,
        bank_account_id: item.bank_account_id,
        name: item.name,
        bank_account_number: item.bank_account_number,
        bank_name: item.bank_name || '',
        bank_swift: item.bank_swift || '',
        bank_currency: item.bank_currency,
        show_on_invoice: item.show_on_invoice || false,
        own_bank_account: item.own_bank_account || false,
        factor_bank_account: item.factor_bank_account || false
      }))
      : []
  }

  async loadExistingInvoiceAccounts(signal) {
    if (this.invoiceOriginalAccountIds.length === 0) {
      this.selectedAccounts = []
      return
    }

    await this.loadMissingAccounts(this.invoiceOriginalAccountIds, signal)
  }

  async loadMissingAccounts(accountIds, signal) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = {
        Accept: 'application/json'
      }

      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }

      const params = new URLSearchParams({
        rows: '20',
        name: '',
        include_ids: accountIds.join(',')
      })

      const response = await fetch(`/find_bank_accounts?${params}`, {
        method: 'GET',
        headers,
        signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      const missingAccounts = Array.isArray(data)
        ? data.map(item => ({
          id: item.bank_account_version_id || item.id,
          bank_account_version_id: item.bank_account_version_id || item.id,
          bank_account_id: item.bank_account_id,
          name: item.name,
          bank_account_number: item.bank_account_number,
          bank_name: item.bank_name || '',
          bank_swift: item.bank_swift || '',
          bank_currency: item.bank_currency,
          show_on_invoice: item.show_on_invoice || false,
          own_bank_account: item.own_bank_account || false,
          factor_bank_account: item.factor_bank_account || false
        })).filter(account =>
          accountIds.includes(account.bank_account_version_id)
        )
        : []

      missingAccounts.forEach(account => {
        const exists = this.selectedAccounts.some(selected =>
          selected.bank_account_version_id === account.bank_account_version_id
        )
        if (!exists) {
          this.selectedAccounts.push(account)
        }
      })
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error("Error loading missing accounts:", error)
    }
  }

  async onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      await this.triggerSearch({ target: this.inputTarget })
    } else {
      await this.showAllAvailableAccounts()
    }
  }

  async showAllAvailableAccounts() {
    try {
      const accounts = await this.fetchBankAccounts("")
      this.showSearchResults(accounts)
    } catch (error) {
      console.error("Error loading all accounts:", error)
      this.hideSearchResults()
    }
  }

  showSearchResults(accounts) {
    this.listTarget.innerHTML = ""

    const fragment = document.createDocumentFragment()
    accounts.forEach((account, index) => {
      const html = this.searchResultTemplate({
        index,
        ...account
      })
      const temp = document.createElement('div')
      temp.innerHTML = html
      fragment.appendChild(temp.firstElementChild)
    })
    this.listTarget.appendChild(fragment)

    this.menuTarget.classList.remove("hidden")
  }

  hideSearchResults() {
    this.menuTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
  }

  // Akcja wisi na trwałym kontenerze listy (`list`), nie na pozycjach: pozycje wstawia
  // showSearchResults z szablonu, a Stimulus wiąże `data-action` nowych węzłów dopiero po
  // przetworzeniu mutacji przez MutationObserver — klik w pozycję trafiony w tym okienku
  // przepada bez śladu. Klikniętą pozycję wyznaczamy przez closest, bo przy delegacji
  // event.params należałoby do węzła z akcją (listy), a nie do pozycji.
  selectAccount(event) {
    const option = event.target.closest("[data-dropdown-invoice-bank-account-version-id-param]")
    if (!option) return // klik w margines listy albo poza pozycją

    event.preventDefault()
    event.stopPropagation()
    this.loadAbortController?.abort()
    const versionId = Number(option.dataset.dropdownInvoiceBankAccountVersionIdParam)
    const selectedAccount = this.availableAccounts.filter(account =>
      account.bank_account_version_id === versionId
    )

    this.selectedAccounts.push(selectedAccount[0])
    this.updateDisplay()
    this.inputTarget.value = ""

    requestAnimationFrame(() => {
      this.inputTarget.blur()
      this.hideSearchResults()
    })
  }

  // Ta sama delegacja co w selectAccount — przycisk usuwania wstawia updateSelectedList
  // z szablonu, więc akcja wisi na trwałym kontenerze `selectedList`.
  removeAccount(event) {
    const option = event.target.closest("[data-dropdown-invoice-bank-account-version-id-param]")
    if (!option) return // klik w treść wiersza, poza przyciskiem usuwania

    const versionId = Number(option.dataset.dropdownInvoiceBankAccountVersionIdParam)
    this.selectedAccounts = this.selectedAccounts.filter(account =>
      account.bank_account_version_id !== versionId
    )
    this.updateDisplay()
  }

  getAvailableAccounts() {
    const selectedIds = this.selectedAccounts.map(account => account.bank_account_version_id)
    return this.availableAccounts.filter(account => !selectedIds.includes(account.bank_account_version_id))
  }

  updateDisplay() {
    this.updateSelectedList()
    this.updateHiddenInputs()
    this.updateRemoveButtonVisibility()
  }

  updateRemoveButtonVisibility() {
    const isLastAccount = this.selectedAccounts.length <= 1
    const deptHasShowOnInvoice = this.availableAccounts.some(a => a.show_on_invoice)
    const shouldHide = this.newRecordValue && isLastAccount && deptHasShowOnInvoice

    this.selectedListTarget.querySelectorAll("button[data-dropdown-invoice-bank-account-version-id-param]").forEach(btn => {
      btn.classList.toggle("hidden", shouldHide)
    })
  }

  updateSelectedList() {
    this.selectedListTarget.innerHTML = ""

    if (this.selectedAccounts.length === 0) {
      return
    }

    const fragment = document.createDocumentFragment()
    this.selectedAccounts.forEach(account => {
      const html = this.selectedTemplate(account)
      const temp = document.createElement('div')
      temp.innerHTML = html
      fragment.appendChild(temp.firstElementChild)
    })
    this.selectedListTarget.appendChild(fragment)
  }

  updateHiddenInputs() {
    this.hiddenInputsTarget.innerHTML = ""

    if (this.selectedAccounts.length === 0) {
      const input = this.hiddenInputTemplateTarget.content.cloneNode(true).querySelector('input')
      input.value = ""
      this.hiddenInputsTarget.appendChild(input)
    } else {
      const fragment = document.createDocumentFragment()
      this.selectedAccounts.forEach(account => {
        const input = this.hiddenInputTemplateTarget.content.cloneNode(true).querySelector('input')
        input.value = account.bank_account_version_id
        fragment.appendChild(input)
      })
      this.hiddenInputsTarget.appendChild(fragment)
    }
  }

  disconnect() {
    this.loadAbortController?.abort()
  }

  clickOutside(event) {
    event.preventDefault()
    if (this.inputTarget.contains(event.target)) return

    if (!this.hasMenuTarget) return

    if (!this.menuTarget.classList.contains("hidden")) {
      this.hideSearchResults()
      this.inputTarget.blur()
    }
  }

  selectCreatedAccount(event) {
    const newAccount = event.detail.bank_account
    if (newAccount) {
      this.selectedAccounts.push(newAccount)
      this.updateDisplay()
      this.inputTarget.value = ""
    } else { console.error("Error added new account") }
  }
}
